import store from '@/store'
import debug from '@/helpers/debug'
import { LANDING_PAGE } from '@/types'

import {
  SET_REQUEST_LOADING,
  SET_CALLBACK_LANDING_PAGE,
  RESET_CALLBACK_LANDING_PAGE,
  SET_LOGIN_METHOD,
  SET_AUTH_INTENT,
} from '@/store/mutation-types'
import { writeAuthIntent, type AuthIntent } from '@/helpers/authIntent'
import {
  clearMockOnboarding,
  getMockOnboarding,
} from '@/mock-api-server/mock-onboarding-state'
import { LoginMethod } from '@/types'
import { loginReturnRouteName } from '@/router/loginReturn'

export const debugRouterGuard = (to: any, from: any) => {
  debug('[ROUTER GUARDS] debugRouterGuards', Date.now())
  return true
}

export const requiresUserToBeUnauthenticated = (to: any, from: any) => {
  debug('[ROUTER GUARDS] requiresUserToBeUnauthenticated', { to, from, store })

  if (import.meta.env.MODE === 'mockapi') {
    debug('[ROUTER GUARDS] Mock API is enabled')
    return true
  }

  // @ts-ignore
  if (store.state.auth.authenticated) {
    return _redirectUserToRequestedPage(to)
  }

  return true
}

export const requiresUserToBeAuthenticated = (to: any, from: any) => {
  debug('[ROUTER GUARDS] requiresUserToBeAuthenticated', { to, from, store })

  if (import.meta.env.MODE === 'mockapi') {
    return true
  }

  // @ts-ignore
  if (store.state.auth.authenticated) {
    return true
  }

  // A signed-out email link such as /dashboard/my-tax-relief should come
  // back to that page after login. Auth0 only carries the route name.
  const returnRoute = loginReturnRouteName(to)
  if (returnRoute) {
    store.commit(SET_CALLBACK_LANDING_PAGE, returnRoute)
  }

  return { name: 'Login' }
}

export const handleSignUpState = (to: any, from: any) => {
  debug('[ROUTER GUARDS] handleSignUpState', { to, from, store })

  // Send a user to the correct landing page
  // if not completed name & address.
  if (
    // @ts-ignore
    store.state.donor.userDetails.landingPage === LANDING_PAGE.NAME_ADDRESS &&
    to.name !== 'DONOR_FLOW_ManageAccountCreateDetails'
  ) {
    return { name: 'DONOR_FLOW_ManageAccountCreateDetails' }
  }

  // Send a user to the correct landing page
  // if not agreed to declaration.
  if (
    // @ts-ignore
    store.state.donor.userDetails.landingPage === LANDING_PAGE.QUESTIONS &&
    to.name !== 'DONOR_FLOW_ManageAccountCreateDeclaration'
  ) {
    // Browser back (or any navigate) from Authorise → Details is intentional:
    // reset landing so the QUESTIONS guard does not bounce them forward again.
    if (
      to.name === 'DONOR_FLOW_ManageAccountCreateDetails' &&
      from.name === 'DONOR_FLOW_ManageAccountCreateDeclaration'
    ) {
      store.commit('SET_LANDING_PAGE', LANDING_PAGE.NAME_ADDRESS)
      return true
    }
    return { name: 'DONOR_FLOW_ManageAccountCreateDeclaration' }
  }

  return true
}

export const setRequestLoadingFalse = (to: any, from: any) => {
  debug('[ROUTER GUARDS] setRequestLoadingFalse', { to, from })

  store.commit(SET_REQUEST_LOADING, false)

  return true
}

export const redirectOnAuthCallbackError = (to: any, from: any) => {
  debug('[ROUTER GUARDS] redirectOnAuthCallbackError', { to, from })

  if (to.hash.includes('error_description')) {
    debug('An authentication error occurred', to.hash)
    return { name: 'Login' }
  }

  return true
}

export const storeRequestedPage = (to: any, from: any) => {
  debug('[ROUTER GUARDS] storeRequestedPage', { to, from })

  store.commit(SET_CALLBACK_LANDING_PAGE, to.name)

  return true
}

/** Clear the stored landing page once navigation has reached it. */
export const clearReachedCallbackLandingPage = (
  to: any,
  from: any,
  failure?: any
) => {
  // @ts-ignore
  const callbackLandingPage = store.state.auth.callbackLandingPage
  if (failure || !callbackLandingPage || to.name !== callbackLandingPage) {
    return
  }

  debug('[ROUTER GUARDS] clearReachedCallbackLandingPage', { to, from })
  store.commit(RESET_CALLBACK_LANDING_PAGE)
}

export const catchAuth0CallbackQueryParam = (to: any, from: any) => {
  debug('[ROUTER GUARDS] catchAuth0CallbackQueryParam', { to, from })

  if (to.query.callbacklandingpage) {
    store.commit(SET_CALLBACK_LANDING_PAGE, to.query.callbacklandingpage)
  }

  // Restore signup/login intent from the Auth0 return URL (survives magic links).
  const authIntent = to.query.authintent
  if (authIntent === 'signup' || authIntent === 'login') {
    store.commit(SET_AUTH_INTENT, authIntent as AuthIntent)
    writeAuthIntent(authIntent)
  }

  return true
}

export const checkLoginMethod = (to: any, from: any) => {
  // Legacy support links: send ?loginmethod=link to the dedicated URL.
  if (
    to.query.loginmethod === 'link' &&
    (to.name === 'Login' || to.name === 'Signup')
  ) {
    const query = { ...to.query }
    delete query.loginmethod
    return {
      name: to.name === 'Signup' ? 'SignupMagicLink' : 'LoginMagicLink',
      query,
      replace: true,
    }
  }

  // Prefer route meta (dedicated /login/magic-link URLs), then query.
  const metaMethod = to.meta?.loginMethod
  if (metaMethod === LoginMethod.LINK || metaMethod === 'link') {
    store.commit(SET_LOGIN_METHOD, LoginMethod.LINK)
  } else if (metaMethod === LoginMethod.CODE || metaMethod === 'code') {
    store.commit(SET_LOGIN_METHOD, LoginMethod.CODE)
  } else if (to.query.loginmethod === 'link') {
    store.commit(SET_LOGIN_METHOD, LoginMethod.LINK)
  } else if (to.query.loginmethod === 'code') {
    store.commit(SET_LOGIN_METHOD, LoginMethod.CODE)
  }

  return true
}

/** Persist login vs signup intent before Auth0 round-trip. */
export const setAuthIntentFromRoute = (to: any) => {
  const intent = to.meta.authIntent === 'signup' ? 'signup' : 'login'
  store.commit('SET_AUTH_INTENT', intent)

  // Mock signup: drop a completed onboarding donor so /signup always starts at
  // details → authorise, instead of resolving GET /me as landingPage 100.
  if (intent === 'signup' && import.meta.env.MODE === 'mockapi') {
    const existing = getMockOnboarding()
    if (existing && existing.landingPage >= 100) {
      clearMockOnboarding()
    }
  }

  return true
}

const _redirectUserToRequestedPage = (to: any) => {
  // @ts-ignore
  const callbackLandingPage = store.state.auth.callbackLandingPage
  if (callbackLandingPage && to.name !== callbackLandingPage) {
    return { name: callbackLandingPage }
  } else {
    return { name: 'Dashboard' }
  }
}
